import { Component, EventEmitter, Input, Output, OnInit, OnDestroy, Optional } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription, take } from 'rxjs';

import { TranslateService } from '../../../../../services/translate.service';
import { DirectionService } from '../../../../../services/direction.service';
import { ViewScopeService } from '../../../../../services/view-scope.service';
import { BasicInfoService } from './basic-info.service';
import { SelectItem } from '../../../../../models/selected-item.model';
import { CreateAccountService } from '../../create-account.service';

import { RefreshService } from '../../../../../services/refresh.service';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { SelectAccountComponent } from '../../../select-account/select-account.component';

// PrimeNG Modules
import { RadioButtonModule } from 'primeng/radiobutton';
import { InputTextModule } from 'primeng/inputtext';
import { DropdownModule } from 'primeng/dropdown';
import { CheckboxModule } from 'primeng/checkbox';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api'; // For Toast messages
import { AutoCompleteModule } from 'primeng/autocomplete'; // Import AutoCompleteModule
import { DatePickerModule } from 'primeng/datepicker'; // Import DatePickerModule

@Component({
	selector: 'app-basic-info',
	standalone: true,
	imports: [
		CommonModule,
		FormsModule,
		RadioButtonModule,
		InputTextModule,
		DropdownModule,
		CheckboxModule,
		ButtonModule,
		ToastModule,
		AutoCompleteModule,
		DatePickerModule
	],
	templateUrl: './basic-info.component.html',
	styleUrls: ['./basic-info.component.scss'], // You can add specific styles here if needed
	providers: [MessageService] // Provide MessageService locally if needed, or from root
})
export class BasicInfoComponent implements OnInit, OnDestroy {
	
	// Inputs from the parent component (CreateAccountComponent)
	@Input() entityType: SelectItem | null = null;
	@Input() businessName: string = '';
	@Input() businessType: SelectItem | string | null = null;
	@Input() businessNumber: string = '';
	@Input() isActive: boolean = true;
	@Input() creationSuccess: boolean = false;
	@Input() createdId: number | null = null;
	@Input() isFastMode: boolean = false;  // When true, enables fast-mode behavior for this step (default: false)

	// Outputs to the parent component when data changes or navigation occurs
	@Output() entityTypeChange = new EventEmitter<SelectItem | null>();
	@Output() businessNameChange = new EventEmitter<string>();
	@Output() businessTypeChange = new EventEmitter<SelectItem | string | null>();
	@Output() businessNumberChange = new EventEmitter<string>();
	@Output() isActiveChange = new EventEmitter<boolean>();
	@Output() nextStep = new EventEmitter<void>();
	@Output() save = new EventEmitter<void>();

	
	expenseCode: string 		= ''; 	// Expense code for Customer entity
	withholdingTaxCode: string 	= '';	// Code of withholding tax.
	withholdingAccount: string 	= '';   // Account number of withholding tax.


	// For "Is Company Active in Real Life" (activity type)
	isCompanyActive: SelectItem | string | null = null;
	activityTypeOptions: SelectItem[] = [];
	filteredActivityTypes: any[] = [];
	activityTypeSub?: Subscription;

	// Dropdown options for Business Type
	businessTypeOptions: SelectItem[] = [];
	businessTypeSub?: Subscription;

	// Dropdown options for Entity Type
	entityTypeOptions: SelectItem[] = [];
	entityTypeSub?: Subscription;

	// Object to hold validation error messages
	formErrors: { [key: string]: string } = {};

	// Additional properties
	filteredBusinessTypes: any[] = [];
	filteredEntityTypes: SelectItem[] = [];
	contactName: string = '';
	contactTitle: string = '';
	ownerFirstName: string = '';
	ownerLastName: string = '';
	establishedDate: Date | null = null;
	unionBusinessId: string = '';
	note: string = '';
	description: string = '';
	smallExpenseValue: number | null = 300.00;
	isApproved: boolean = true;
	isCustomerEntityType: boolean = false;

	constructor(
		private messageService: MessageService,
		public translateServ: TranslateService,
		public dirServ: DirectionService,
		private basicInfoService: BasicInfoService,
		private viewScopeService: ViewScopeService,
		private createAccountService: CreateAccountService,
		private refreshService: RefreshService,
		@Optional() public dialogConfig?: DynamicDialogConfig,
		@Optional() public dialogRef?: DynamicDialogRef
		) { }

	ngOnInit(): void {
		// If opened via DynamicDialog with data, allow passing isFastMode
		if (this.dialogConfig && this.dialogConfig.data && typeof this.dialogConfig.data.isFastMode !== 'undefined') {
			this.isFastMode = !!this.dialogConfig.data.isFastMode;
			// fast-mode: set defaults and lock down fields
			if (this.isFastMode) {
				// Force entityType to be supplier (code 200) and emit change
				this.basicInfoService.getSpecificOptionFromAC(200).pipe(take(1)).subscribe(item => {
					if (item) {
						this.entityType = item;
						this.entityTypeChange.emit(this.entityType as SelectItem | null);
						this.updateIsCustomerEntityType();
					}
				});
				// Set default activity option
				this.basicInfoService.getSpecificActivityOption().pipe(take(1)).subscribe(opt => {
					if (opt) {
						this.isCompanyActive = opt;
					}
				});
			}
		}
		// Load business type options dynamically
		this.businessTypeSub = this.basicInfoService.getBusinessTypeData().subscribe(options => {
			this.businessTypeOptions = options;
		});
		// Load entity type options dynamically
		this.entityTypeSub = this.basicInfoService.getAccountTypeData().subscribe(options => {
			this.entityTypeOptions = options;
			this.updateIsCustomerEntityType();
		});

		// Load activity type options dynamically (is company active in real life)
		this.activityTypeSub = this.basicInfoService.getActivityType().subscribe(options => {
			this.activityTypeOptions = options;
		});

		// Restore from sessionStorage if exists (use consistent key)
		const tabKey = this.constructor.name;
		if (this.createAccountService.doWeHaveMemory(tabKey)) {
			const saved = this.createAccountService.readObject(tabKey);
			if (saved) {
				// Restore all known fields
				this.entityType 		= saved.entityType ?? this.entityType;
				this.businessName 		= saved.businessName ?? this.businessName;
				this.businessType 		= saved.businessType ?? this.businessType;
				this.businessNumber 	= saved.businessNumber ?? this.businessNumber;
				this.isActive 			= saved.isActive ?? this.isActive;
				this.expenseCode 		= saved.expenseCode ?? this.expenseCode;
				this.withholdingTaxCode = saved.withholdingTaxCode ?? this.withholdingTaxCode;
				this.withholdingAccount = saved.withholdingAccount ?? this.withholdingAccount;
				this.isCompanyActive 	= saved.isCompanyActive ?? this.isCompanyActive;
				this.contactName 		= saved.contactName ?? this.contactName;
				this.contactTitle 		= saved.contactTitle ?? this.contactTitle;
				this.ownerFirstName 	= saved.ownerFirstName ?? this.ownerFirstName;
				this.ownerLastName 		= saved.ownerLastName ?? this.ownerLastName;
				this.establishedDate 	= saved.establishedDate ?? this.establishedDate;
				this.unionBusinessId 	= saved.unionBusinessId ?? this.unionBusinessId;
				this.note 				= saved.note ?? this.note;
				this.description 		= saved.description ?? this.description;
				this.smallExpenseValue 	= saved.smallExpenseValue ?? this.smallExpenseValue;
				this.isApproved 		= saved.isApproved ?? this.isApproved;
				this.createdId 			= saved.createdId ?? null;
				this.creationSuccess 	= typeof this.createdId === 'number';

				if(this.entityType) this.entityTypeChange.emit(this.entityType);
			}
			this.updateIsCustomerEntityType();		
		}
	}

	ngOnDestroy(): void {
		this.businessTypeSub?.unsubscribe();
		this.entityTypeSub?.unsubscribe();
		this.activityTypeSub?.unsubscribe();
	}

	/**
	 * Handles changes to entityType and emits the updated value.
	 * @param value The new entity type.
	 */
	onEntityTypeChange(event: any): void {
		// PrimeNG AutoComplete emits { originalEvent, value }
		this.entityType = event && event.value ? event.value : null;
		this.entityTypeChange.emit(this.entityType as SelectItem | null);
		this.updateIsCustomerEntityType();
		// Persist entityType to session using tabKey
		const tabKey = this.constructor.name;
		let prev = this.createAccountService.readObject(tabKey) || {};
		this.createAccountService.memorizeAnObject(tabKey, {
			...prev,
			entityType: this.entityType
		});
	}

	/**
	 * Handles changes to businessName and emits the updated value.
	 * @param value The new business name.
	 */
	onBusinessNameChange(value: string): void {
		this.businessName = value;
		this.businessNameChange.emit(this.businessName);
	}

	/**
	 * Handles changes to businessType and emits the updated value.
	 * @param value The new business type.
	 */
	onBusinessTypeChange(value: SelectItem | string | null): void {
		this.businessType = value;
		this.businessTypeChange.emit(this.businessType);
	}

	/**
	 * Handles changes to businessNumber and emits the updated value.
	 * @param value The new business number.
	 */
	onBusinessNumberChange(value: string): void {
		this.businessNumber = value;
		this.businessNumberChange.emit(this.businessNumber);
	}

	/**
	 * Handles changes to isActive and emits the updated value.
	 * @param value The new isActive status.
	 */
	onIsActiveChange(value: boolean): void {
		this.isActive = value;
		this.isActiveChange.emit(this.isActive);
	}

	/**
	 * Validates the fields and emits nextStep if valid.
	 */
	validateAndProceed(): void {
		let isCustomer = false;
		if (this.entityType) {
			isCustomer = this.entityType.code === '1';
		}

		this.formErrors = {}; // Clear previous errors

		let isValid = true;

		// Validate required fields
		if (!this.entityType) {
			this.formErrors['entityType'] = this.translateServ.dictionary['STEP_BASIC_TYPE_REQ'];
			isValid = false;
		}
		if (!this.businessName || !this.businessName.trim()) {
			isValid = false;
		}
		if (!this.businessType) {
			this.formErrors['businessType'] = this.translateServ.dictionary['STEP_BASIC_BUSS_TYPE_REQ'];
			isValid = false;
		}
		if (!this.businessNumber || !this.businessNumber.trim()) {
			this.formErrors['businessNumber'] = this.translateServ.dictionary['STEP_BASIC_BUSS_NUM_REQ'];
			isValid = false;
		}

		// Validate isCompanyActive (activity type)
		if (!this.isCompanyActive) {
			this.formErrors['isCompanyActive'] = 'Please select if the company is active in real life.';
			isValid = false;
		}

		if (!isValid) {
			// Display a toast message for validation errors
			this.messageService.add({
				severity: 'error',
				summary: 'Validation Error',
				detail: 'Please fill in all required fields for Basic Information.',
				life: 3000
			});
			return;
		}

		// Always send only the code for CustomerSupplierStatusRecID (never the name)
		// Always send only the code for BusinessTypeRecID and CustomerSupplierTypeRecID
		let selectedBusinessTypeCode = '';
		if (this.businessType && typeof this.businessType === 'object' && this.businessType !== null && 'code' in this.businessType) {
			selectedBusinessTypeCode = (this.businessType as SelectItem).code;
		} else if (typeof this.businessType === 'string') {
			// Try to find the code from the options if a string is given
			const found = this.businessTypeOptions.find(opt => opt.label === this.businessType || opt.value === this.businessType);
			selectedBusinessTypeCode = found ? found.code : this.businessType;
		}

		let selectedEntityTypeCode = '';
		if (this.entityType) {
			selectedEntityTypeCode = this.entityType.code;
		}
		let selectedActivityTypeCode = '';
		if (this.isCompanyActive && typeof this.isCompanyActive === 'object' && this.isCompanyActive !== null && 'code' in this.isCompanyActive)
		{
			selectedActivityTypeCode = (this.isCompanyActive as SelectItem).code;
		} else if (typeof this.isCompanyActive === 'string') {
			// If it's a string, try to find the code from the options (fallback)
			const found = this.activityTypeOptions.find(opt => opt.label === this.isCompanyActive || opt.value === this.isCompanyActive);
			selectedActivityTypeCode = found ? found.code : this.isCompanyActive;
		}

		// Format establishedDate as yyyy-MM-dd if present
		let formattedDate: string | null = null;
		if (this.establishedDate instanceof Date && !isNaN(this.establishedDate.getTime())) {
			const year = this.establishedDate.getFullYear();
			const month = (this.establishedDate.getMonth() + 1).toString().padStart(2, '0');
			const day = this.establishedDate.getDate().toString().padStart(2, '0');
			formattedDate = `${year}-${month}-${day}`;
		}

		// Build the required JSON structure for backend
		const payload = {
			CustomerSupplierStatusRecID: selectedActivityTypeCode || '', // Always code
			Notes: this.note || '',
			CustomerName: this.businessName || '',
			ContactName: this.contactName || '',
			FirstName: this.ownerFirstName || '',
			LastName: this.ownerLastName || '',
			ContactTitle: this.contactTitle || '',
			TermsConditions: '',
			ID: '',
			Business_ID: this.businessNumber || '',
			BusinessTypeRecID: selectedBusinessTypeCode || '', // Always code
			CustomerSupplierTypeRecID: selectedEntityTypeCode || '', // Always code
			DateIncorporation: formattedDate,
			customer_vat_expences_num: isCustomer ? (this.expenseCode  || null) : null,
			WithholdingTax : isCustomer ? (this.withholdingTaxCode || null) : null,
			WithholdingTaxAccount : isCustomer ? (this.withholdingAccount || null) : null,
			CompanyRecID: this.viewScopeService.getSelectedCompanyId()?.toString(),
			Descr: this.description || '',
			IsActive: this.isActive ? 1 : 0,
			IsApproved: this.isApproved ? 1 : 0,
			MaxVatSmallExpenses: this.smallExpenseValue ?? 0,
			ParentCustomerSupplierRecID: this.unionBusinessId && this.unionBusinessId.trim() !== '' ? this.unionBusinessId : null,
			cust_RecId : this.viewScopeService.getCurrentCustomer(),
			UserRecID: this.viewScopeService.getCurrentOpertaingUserId().toString()
		};
		// Print the structure to be sent to the API
		console.log('Payload to API:', payload);

		// Call the service to create the account
		this.basicInfoService.createAnAccount(payload).subscribe({
			next: (result: any) => {
				console.log('API response:', result);
				if (typeof result === 'number') {
					this.nextStep.emit();
					this.messageService.clear();
				} else {
					this.messageService.add({
						severity: 'error',
						summary: 'Error',
						detail: 'Failed to create account. Please try again.',
						life: 3000
					});
					console.warn('API did not return a number, cannot proceed to next step. Response:', result);
				}
			},
			error: (err: any) => {
				console.error('API error:', err);
				this.messageService.add({
					severity: 'error',
					summary: 'Error',
					detail: 'Failed to create account. Please try again.',
					life: 3000
				});
			}
		});
	}

	// Filtering for Business Type autocomplete
	filterBusinessTypes(event: any) {
		const query = event.query;
		this.filteredBusinessTypes = this.businessTypeOptions.filter(option =>
			option.label && option.label.includes(query)
		);
	}

	// Filtering for Entity Type autocomplete
	filterEntityTypes(event: any) {
		const query = event.query;
		this.filteredEntityTypes = this.entityTypeOptions.filter((option: SelectItem) =>
			option.label && option.label.includes(query)
		);
	}

	// Filtering for Activity Type autocomplete
	filterActivityTypes(event: { query: string }): void {
		const query = event?.query || '';
		this.filteredActivityTypes = this.activityTypeOptions.filter(option =>
			option.label && option.label.includes(query)
		);
	}

	nextStepHandler(): void {
		// Save current form data to sessionStorage using consistent key
		const tabKey = this.constructor.name;
		// Prevent overwriting businessId/businessNumber if already set in sessionStorage
		let prev = this.createAccountService.readObject(tabKey) || {};
		let businessIdToSave = prev.businessId && prev.businessId.trim() ? prev.businessId : this.businessNumber;
		let businessNumberToSave = prev.businessNumber && prev.businessNumber.trim() ? prev.businessNumber : this.businessNumber;
		this.createAccountService.memorizeAnObject(tabKey, {
			entityType: this.entityType,
			businessName: this.businessName,
			businessId: businessIdToSave,
			businessType: this.businessType,
			businessNumber: businessNumberToSave,
			isActive: this.isActive,
			expenseCode: this.expenseCode,
			WithholdingTax : this.withholdingTaxCode,
			WithholdingTaxAccount : this.withholdingAccount,
			isCompanyActive: this.isCompanyActive,
			contactName: this.contactName,
			contactTitle: this.contactTitle,
			ownerFirstName: this.ownerFirstName,
			ownerLastName: this.ownerLastName,
			establishedDate: this.establishedDate,
			unionBusinessId: this.unionBusinessId,
			note: this.note,
			description: this.description,
			smallExpenseValue: this.smallExpenseValue,
			isApproved: this.isApproved,
			creationSuccess: this.creationSuccess,
			createdId: this.createdId
		});
		if (this.creationSuccess) {
			this.nextStep.emit();
		}
	}

	/**
	 * Complete the fast-mode process: clear memory and notify parent to save.
	 */
	public completeProcess(): void {
		// Only allow completing when creation succeeded and we have an id
		if (!this.creationSuccess || !this.createdId) {
			this.messageService.add({ severity: 'warn', summary: 'Not ready', detail: 'Please create the account first.', life: 3000 });
			return;
		}
		// Clear memory
		this.createAccountService.cleanMemory();
		// If opened as a dialog, close with the created id so caller receives it
		if (this.isFastMode && this.dialogRef) {
			console.log('[BasicInfo] completeProcess: closing dialog with id', this.createdId);
			this.dialogRef.close(this.createdId);
		} else {
			this.save.emit();
		}
	}

	updateIsCustomerEntityType(): void {
		this.isCustomerEntityType = !!this.entityType && this.entityType.code === '100';
	}

	/**
	 * Triggers account creation and sets creationSuccess/createdId on success.
	 */
	createAccount(): void {
		let isCustomer = false;
		if (this.entityType) {
			isCustomer = this.entityType.code === '100';
		}
		this.formErrors = {};
		let isValid = true;
		if (!this.entityType) {
			this.formErrors['entityType'] = this.translateServ.dictionary['STEP_BASIC_TYPE_REQ'];
			isValid = false;
		}
		if (!this.businessName || !this.businessName.trim()) {
			this.formErrors['businessName'] = this.translateServ.dictionary['STEP_BASIC_BUSS_NAME_REQ'];
			isValid = false;
		}
		if (!this.businessType) {
			this.formErrors['businessType'] = this.translateServ.dictionary['STEP_BASIC_BUSS_TYPE_REQ'];
			isValid = false;
		}
		if (!this.businessNumber || !this.businessNumber.trim()) {
			this.formErrors['businessNumber'] = this.translateServ.dictionary['STEP_BASIC_BUSS_NUM_REQ'];
			isValid = false;
		}
		if (!this.isCompanyActive) {
			this.formErrors['isCompanyActive'] = 'Please select if the company is active in real life.';
			isValid = false;
		}
		// Require Input VAT if entityType is Customer (code === '100')
		if (this.isEntityTypeCustomer() && (!this.expenseCode || !this.expenseCode.trim())) 
		{
			this.formErrors['expenseCode'] = this.translateServ.dictionary['INPUT_VAT_REQ'] || 'Input VAT is required for Customer.';
			isValid = false;
		}

		if (!isValid) {
			this.messageService.add({
				severity: 'error',
				summary: 'Validation Error',
				detail: 'Please fill in all required fields for Basic Information.',
				life: 3000
			});
			return;
		}
		let selectedBusinessTypeCode = '';
		if (this.businessType && typeof this.businessType === 'object' && this.businessType !== null && 'code' in this.businessType) {
			selectedBusinessTypeCode = (this.businessType as SelectItem).code;
		} else if (typeof this.businessType === 'string') {
			const found = this.businessTypeOptions.find(opt => opt.label === this.businessType || opt.value === this.businessType);
			selectedBusinessTypeCode = found ? found.code : this.businessType;
		}
		let selectedEntityTypeCode = '';
		let selectedEntityTypeRecId: number | string | null = null;
		if (this.entityType) {
			selectedEntityTypeCode = this.entityType.code;
			selectedEntityTypeRecId = this.getEntityTypeRecIdByCode(selectedEntityTypeCode);
		}
		let selectedActivityTypeCode = '';
		if (this.isCompanyActive && typeof this.isCompanyActive === 'object' && this.isCompanyActive !== null && 'code' in this.isCompanyActive) {
			selectedActivityTypeCode = (this.isCompanyActive as SelectItem).code;
		} else if (typeof this.isCompanyActive === 'string') {
			const found = this.activityTypeOptions.find(opt => opt.label === this.isCompanyActive || opt.value === this.isCompanyActive);
			selectedActivityTypeCode = found ? found.code : this.isCompanyActive;
		}
		let formattedDate: string | null = null;
		if (this.establishedDate instanceof Date && !isNaN(this.establishedDate.getTime())) {
			const year = this.establishedDate.getFullYear();
			const month = (this.establishedDate.getMonth() + 1).toString().padStart(2, '0');
			const day = this.establishedDate.getDate().toString().padStart(2, '0');
			formattedDate = `${year}-${month}-${day}`;
		}
		const payload = {
			CustomerSupplierStatusRecID: selectedActivityTypeCode || '',
			Notes: this.note || '',
			CustomerName: this.businessName || '',
			ContactName: this.contactName || '',
			FirstName: this.ownerFirstName || '',
			LastName: this.ownerLastName || '',
			ContactTitle: this.contactTitle || '',
			TermsConditions: '',
			ID: '',
			Business_ID: this.businessNumber || '',
			BusinessTypeRecID: selectedBusinessTypeCode || '',
			CustomerSupplierTypeRecID: selectedEntityTypeRecId ?? '',
			DateIncorporation: formattedDate,
			customer_vat_expences_num: isCustomer ? (this.expenseCode || null) : null,
			WithholdingTax: isCustomer ? (this.withholdingTaxCode || null) : null,
			WithholdingTaxAccount: isCustomer ? (this.withholdingAccount || null) : null,
			CompanyRecID: this.viewScopeService.getSelectedCompanyId()?.toString(),
			Descr: this.description || '',
			IsActive: this.isActive ? 1 : 0,
			IsApproved: this.isApproved ? 1 : 0,
			MaxVatSmallExpenses: this.smallExpenseValue ?? 0,
			ParentCustomerSupplierRecID: this.unionBusinessId && this.unionBusinessId.trim() !== '' ? this.unionBusinessId : null,
			cust_RecId : this.viewScopeService.getCurrentCustomer(),
			UserRecID: this.viewScopeService.getCurrentOpertaingUserId()
		};
		console.log('Payload to API:', JSON.stringify(payload, null, 2));
		this.basicInfoService.createAnAccount(payload).subscribe({
			next: (result: any) => {
				console.log('API response:', result);
				if (typeof result === 'number') {
					this.creationSuccess = true;
					this.createdId = result;
					// Persist state to sessionStorage after successful creation
					const tabKey = this.constructor.name;
					// Prevent overwriting businessId/businessNumber if already set in sessionStorage
					let prev = this.createAccountService.readObject(tabKey) || {};
					let businessIdToSave = prev.businessId && prev.businessId.trim() ? prev.businessId : this.businessNumber;
					let businessNumberToSave = prev.businessNumber && prev.businessNumber.trim() ? prev.businessNumber : this.businessNumber;
					this.createAccountService.memorizeAnObject(tabKey, {
						entityType: this.entityType,
						businessName: this.businessName,
						businessType: this.businessType,
						businessNumber: businessNumberToSave,
						isActive: this.isActive,
						expenseCode: this.expenseCode,
						WithholdingTax: this.withholdingTaxCode,
						WithholdingTaxAccount: this.withholdingAccount,
						isCompanyActive: this.isCompanyActive,
						contactName: this.contactName,
						contactTitle: this.contactTitle,
						ownerFirstName: this.ownerFirstName,
						ownerLastName: this.ownerLastName,
						establishedDate: this.establishedDate,
						unionBusinessId: this.unionBusinessId,
						note: this.note,
						description: this.description,
						smallExpenseValue: this.smallExpenseValue,
						isApproved: this.isApproved,
						creationSuccess: this.creationSuccess,
						createdId: this.createdId
					});
					// this.forcedCustomerSelection.emit(this.createdId);
					const selectAccountComponent = new SelectAccountComponent(this.translateServ, this.viewScopeService, this.refreshService);
					selectAccountComponent.forcedCustomerSelection(this.createdId); // Set the new Id as the Id that we will work with.
					this.messageService.add({
						severity: 'success',
						summary: 'Account Created',
						detail: 'The account was created successfully.',
						life: 3000
					});
				} else {
					this.messageService.add({
						severity: 'error',
						summary: 'Error',
						detail: 'Failed to create account. Please try again.',
						life: 3000
					});
					this.creationSuccess = false;
					this.createdId = null;
				}
			},
			error: (err: any) => {
				console.error('API error:', err);
				this.messageService.add({
					severity: 'error',
					summary: 'Error',
					detail: 'Failed to create account. Please try again.',
					life: 3000
				});
				this.creationSuccess = false;
				this.createdId = null;
			}
		});
	}

	/**
	 * Finds the RecID for a given entity type code.
	 * @param code The business code (e.g., '1', '100')
	 * @returns The RecID (number or string) or null if not found
	 */
	getEntityTypeRecIdByCode(code: string): number | string | null {
		const found = this.entityTypeOptions.find(opt => opt.code === code);
		// Update entityType to the found item
		if (found) {
			this.entityType = found;
			this.entityTypeChange.emit(this.entityType);
		}
		// If your SelectItem has a RecID property, return it; otherwise, return null
		return found && 'aditionalData1' in found ? (found as any).aditionalData1 : null;
	}

	// Whether the form should be disabled (after creation)
	get formDisabled(): boolean {
		return !!this.creationSuccess;
	}

	// Handler for p-calendar (onSelect) event
	onCalendarSelect(event: any): void {
		// You can add custom logic here
		console.log('Calendar date selected:', event);
	}

	// Handler for p-calendar (onClick) event
	onCalendarClick(event: any): void {
		// You can add custom logic here
		console.log('Calendar clicked:', event);
	}
	// Helper to check if entityType is Customer (for template)
	isEntityTypeCustomer(): boolean {
		// Defensive: ensure entityType is SelectItem, not string
		if (!this.entityType || typeof this.entityType !== 'object' || !('code' in this.entityType)) {
			return false;
		}
		return this.entityType.code === '100';
	}
}